import type React from "react";
import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { AiAutoFillSection } from "../../components/AiAutoFillSection";
import type { CrmParseTalentResult } from "../../hooks/use-crm-parse-text-api";
import { useCrmParseText } from "../../hooks/use-crm-parse-text-api";
import type { TalentDetail } from "../../hooks/use-talents-api";
import {
  useCreateTalent,
  useTalentDetail,
  useUpdateTalent,
} from "../../hooks/use-talents-api";

type TalentFormData = {
  name: string;
  nameKana: string;
  email: string;
  phone: string;
  affiliation: string;
  employmentType: string;
  partnerCompany: string;
  partnerContact: string;
  nearestStation: string;
  remotePreference: string;
  age: string;
  desiredReward: string;
  availableFrom: string;
  notes: string;
};

const INITIAL_FORM: TalentFormData = {
  name: "",
  nameKana: "",
  email: "",
  phone: "",
  affiliation: "proprietary",
  employmentType: "",
  partnerCompany: "",
  partnerContact: "",
  nearestStation: "",
  remotePreference: "",
  age: "",
  desiredReward: "",
  availableFrom: "",
  notes: "",
};

/**
 * タレント詳細データをフォームデータに変換する
 * @param {object} talent タレント詳細
 * @return {object} フォームデータ
 */
function talentToFormData(talent: TalentDetail): TalentFormData {
  return {
    name: talent.name || "",
    nameKana: talent.nameKana || "",
    email: talent.email || "",
    phone: talent.phone || "",
    affiliation: talent.affiliation || "proprietary",
    employmentType: talent.employmentType || "",
    partnerCompany: talent.partnerCompany || "",
    partnerContact: talent.partnerContact || "",
    nearestStation: talent.nearestStation || "",
    remotePreference: talent.remotePreference || "",
    age: talent.age != null ? String(talent.age) : "",
    desiredReward:
      talent.desiredReward != null ? String(talent.desiredReward) : "",
    availableFrom: talent.availableFrom || "",
    notes: talent.notes || "",
  };
}

/**
 * フォームデータをAPI送信用に変換する
 * @param {object} form フォームデータ
 * @return {object} API送信用データ
 */
function formDataToPayload(form: TalentFormData): Partial<TalentDetail> {
  return {
    name: form.name,
    nameKana: form.nameKana || null,
    email: form.email || null,
    phone: form.phone || null,
    affiliation: form.affiliation || null,
    employmentType: form.employmentType || null,
    partnerCompany:
      form.affiliation === "partner" ? form.partnerCompany || null : null,
    partnerContact:
      form.affiliation === "partner" ? form.partnerContact || null : null,
    nearestStation: form.nearestStation || null,
    remotePreference: form.remotePreference || null,
    age: form.age ? Number(form.age) : null,
    desiredReward: form.desiredReward ? Number(form.desiredReward) : null,
    availableFrom: form.availableFrom || null,
    notes: form.notes || null,
  };
}

/**
 * タレント新規登録・編集フォームページ
 * URLにidパラメータがある場合は編集モード、ない場合は新規登録モード
 * @return {React.ReactElement} タレントフォームページ
 */
const TalentFormPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const isEditMode = !!id;

  const { talent, loading: detailLoading } = useTalentDetail(
    isEditMode ? id : undefined,
  );
  const { createTalent, loading: createLoading } = useCreateTalent();
  const { updateTalent, loading: updateLoading } = useUpdateTalent();
  const { parseTalentText, loading: isParsing } = useCrmParseText();

  const [form, setForm] = useState<TalentFormData>(INITIAL_FORM);

  // 編集モードでタレントデータが取得できたらフォームに反映
  useEffect(() => {
    if (isEditMode && talent) {
      setForm(talentToFormData(talent));
    }
  }, [isEditMode, talent]);

  const handleChange = useCallback(
    (
      e: React.ChangeEvent<
        HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
      >,
    ) => {
      const { name, value } = e.target;
      setForm(prev => ({ ...prev, [name]: value }));
    },
    [],
  );

  const handleAiParse = useCallback(
    async (text: string) => {
      const result: CrmParseTalentResult = await parseTalentText(text);
      setForm(prev => ({
        ...prev,
        name: result.name ?? prev.name,
        nameKana: result.nameKana ?? prev.nameKana,
        email: result.email ?? prev.email,
        phone: result.phone ?? prev.phone,
        affiliation: result.affiliation ?? prev.affiliation,
        employmentType: result.employmentType ?? prev.employmentType,
        partnerCompany: result.partnerCompany ?? prev.partnerCompany,
        nearestStation: result.nearestStation ?? prev.nearestStation,
        remotePreference: result.remotePreference ?? prev.remotePreference,
        age: result.age != null ? String(result.age) : prev.age,
        desiredReward:
          result.desiredReward != null
            ? String(result.desiredReward)
            : prev.desiredReward,
        availableFrom: result.availableFrom ?? prev.availableFrom,
        notes: result.notes ?? prev.notes,
      }));
      toast.success("AIによる自動入力が完了しました");
    },
    [parseTalentText],
  );

  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();

      if (!form.name.trim()) {
        toast.error("名前は必須です");
        return;
      }

      const payload = formDataToPayload(form);

      try {
        if (isEditMode && id) {
          await updateTalent(id, payload);
          toast.success("タレントを更新しました");
          navigate(`/crm/talents/${id}`);
        } else {
          const created = await createTalent(payload);
          toast.success("タレントを登録しました");
          navigate(`/crm/talents/${created.id}`);
        }
      } catch {
        toast.error(
          isEditMode
            ? "タレントの更新に失敗しました"
            : "タレントの登録に失敗しました",
        );
      }
    },
    [form, isEditMode, id, createTalent, updateTalent, navigate],
  );

  if (isEditMode && detailLoading) {
    return (
      <div className="flex justify-center py-8">
        <span className="loading loading-spinner loading-lg" />
      </div>
    );
  }

  const isSaving = createLoading || updateLoading;

  return (
    <div className="p-4 max-w-3xl mx-auto">
      <div className="flex items-center gap-3 mb-4">
        <Link to="/crm/talents" className="btn btn-ghost btn-sm">
          &larr; 一覧へ
        </Link>
        <h1 className="text-2xl font-bold">
          {isEditMode ? "タレント編集" : "タレント新規登録"}
        </h1>
      </div>

      {!isEditMode && (
        <AiAutoFillSection
          loading={isParsing}
          onParse={handleAiParse}
          placeholder={
            "人材のスキルシートやプロフィール情報を貼り付けて「AIで自動入力」を押してください。\n名前・メール・スキル・希望単価などが自動で入力されます。"
          }
        />
      )}

      <form onSubmit={handleSubmit}>
        {/* 基本情報 */}
        <div className="card bg-base-100 shadow-sm mb-4">
          <div className="card-body">
            <h2 className="card-title text-base">基本情報</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="form-control">
                <label className="label">
                  <span className="label-text">
                    名前 <span className="text-error">*</span>
                  </span>
                </label>
                <input
                  type="text"
                  name="name"
                  className="input input-bordered input-sm"
                  value={form.name}
                  onChange={handleChange}
                  required
                />
              </div>
              <div className="form-control">
                <label className="label">
                  <span className="label-text">フリガナ</span>
                </label>
                <input
                  type="text"
                  name="nameKana"
                  className="input input-bordered input-sm"
                  value={form.nameKana}
                  onChange={handleChange}
                />
              </div>
              <div className="form-control">
                <label className="label">
                  <span className="label-text">メールアドレス</span>
                </label>
                <input
                  type="email"
                  name="email"
                  className="input input-bordered input-sm"
                  value={form.email}
                  onChange={handleChange}
                />
              </div>
              <div className="form-control">
                <label className="label">
                  <span className="label-text">電話番号</span>
                </label>
                <input
                  type="tel"
                  name="phone"
                  className="input input-bordered input-sm"
                  value={form.phone}
                  onChange={handleChange}
                />
              </div>
            </div>
          </div>
        </div>

        {/* 所属 */}
        <div className="card bg-base-100 shadow-sm mb-4">
          <div className="card-body">
            <h2 className="card-title text-base">所属</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="form-control">
                <label className="label">
                  <span className="label-text">所属</span>
                </label>
                <div className="flex gap-4">
                  <label className="label cursor-pointer gap-2">
                    <input
                      type="radio"
                      name="affiliation"
                      className="radio radio-sm"
                      value="proprietary"
                      checked={form.affiliation === "proprietary"}
                      onChange={handleChange}
                    />
                    <span className="label-text">自社</span>
                  </label>
                  <label className="label cursor-pointer gap-2">
                    <input
                      type="radio"
                      name="affiliation"
                      className="radio radio-sm"
                      value="partner"
                      checked={form.affiliation === "partner"}
                      onChange={handleChange}
                    />
                    <span className="label-text">パートナー</span>
                  </label>
                </div>
              </div>
              <div className="form-control">
                <label className="label">
                  <span className="label-text">雇用形態</span>
                </label>
                <select
                  name="employmentType"
                  className="select select-bordered select-sm"
                  value={form.employmentType}
                  onChange={handleChange}
                >
                  <option value="">選択してください</option>
                  <option value="full_time">正社員</option>
                  <option value="freelancer">フリーランス</option>
                  <option value="contract">契約社員</option>
                </select>
              </div>
              {form.affiliation === "partner" && (
                <>
                  <div className="form-control">
                    <label className="label">
                      <span className="label-text">パートナー企業名</span>
                    </label>
                    <input
                      type="text"
                      name="partnerCompany"
                      className="input input-bordered input-sm"
                      value={form.partnerCompany}
                      onChange={handleChange}
                    />
                  </div>
                  <div className="form-control">
                    <label className="label">
                      <span className="label-text">パートナー担当者</span>
                    </label>
                    <input
                      type="text"
                      name="partnerContact"
                      className="input input-bordered input-sm"
                      value={form.partnerContact}
                      onChange={handleChange}
                    />
                  </div>
                </>
              )}
            </div>
          </div>
        </div>

        {/* 条件 */}
        <div className="card bg-base-100 shadow-sm mb-4">
          <div className="card-body">
            <h2 className="card-title text-base">条件</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="form-control">
                <label className="label">
                  <span className="label-text">最寄駅</span>
                </label>
                <input
                  type="text"
                  name="nearestStation"
                  className="input input-bordered input-sm"
                  value={form.nearestStation}
                  onChange={handleChange}
                />
              </div>
              <div className="form-control">
                <label className="label">
                  <span className="label-text">リモート希望</span>
                </label>
                <select
                  name="remotePreference"
                  className="select select-bordered select-sm"
                  value={form.remotePreference}
                  onChange={handleChange}
                >
                  <option value="">選択してください</option>
                  <option value="full_remote">フルリモート</option>
                  <option value="hybrid">ハイブリッド</option>
                  <option value="onsite_only">出社</option>
                </select>
              </div>
              <div className="form-control">
                <label className="label">
                  <span className="label-text">年齢</span>
                </label>
                <input
                  type="number"
                  name="age"
                  className="input input-bordered input-sm"
                  value={form.age}
                  onChange={handleChange}
                  min={0}
                  max={100}
                />
              </div>
              <div className="form-control">
                <label className="label">
                  <span className="label-text">希望単価（円）</span>
                </label>
                <input
                  type="number"
                  name="desiredReward"
                  className="input input-bordered input-sm"
                  value={form.desiredReward}
                  onChange={handleChange}
                  min={0}
                />
              </div>
              <div className="form-control">
                <label className="label">
                  <span className="label-text">稼働可能日</span>
                </label>
                <input
                  type="date"
                  name="availableFrom"
                  className="input input-bordered input-sm"
                  value={form.availableFrom}
                  onChange={handleChange}
                />
              </div>
            </div>
          </div>
        </div>

        {/* メモ */}
        <div className="card bg-base-100 shadow-sm mb-4">
          <div className="card-body">
            <h2 className="card-title text-base">メモ</h2>
            <div className="form-control">
              <textarea
                name="notes"
                className="textarea textarea-bordered"
                rows={4}
                value={form.notes}
                onChange={handleChange}
                placeholder="メモを入力..."
              />
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="flex justify-end gap-3">
          <Link
            to={isEditMode ? `/crm/talents/${id}` : "/crm/talents"}
            className="btn btn-ghost btn-sm"
          >
            キャンセル
          </Link>
          <button
            type="submit"
            className="btn btn-primary btn-sm"
            disabled={isSaving}
          >
            {isSaving && (
              <span className="loading loading-spinner loading-xs" />
            )}
            {isEditMode ? "更新" : "登録"}
          </button>
        </div>
      </form>
    </div>
  );
};

export default TalentFormPage;
